<template>
  <v-container>
    <v-col class="text-right">
      <v-btn fab small color="primary" @click="showUploadAttachmentsDialog = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-col>
    <v-container fluid>
      <div
        v-if="currentArtwork.attachments === null || currentArtwork.attachments.length == 0"
        class="text-caption">
        {{ $t('attachments.no_attachments') }}
      </div>
      <v-row dense>
        <v-col
          v-for="(attachment, index) in currentArtwork.attachments"
          :key="index"
          cols="6"
          md="3"
          sm="4">
          <v-card
            tile
            flat
            align="center"
            justify="center"
            class="grey lighten-4">
            <v-img :src="getImageByContentType(attachment.contentType)" width="48" height="48"></v-img>
            <v-card-text class="text-caption">
              {{ attachment.fileName }}
            </v-card-text>
            <v-card-actions>
              <v-tooltip v-if="attachment.isExportable" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-file-export-outline</v-icon>
                </template>
                {{ $t('attachments.is_exportable_hint') }}
              </v-tooltip>
              <v-spacer></v-spacer>
              <v-menu>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on">
                    <v-icon>
                      mdi-pencil-outline
                    </v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="attachment.isExportable" link @click="unsetExportable(attachment)">
                    <v-list-item-icon>
                      <v-icon>mdi-file-export-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('attachments.command_unset_exportable') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else link @click="setExportable(attachment)">
                    <v-list-item-icon>
                      <v-icon>mdi-file-export-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('attachments.command_set_exportable') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item link @click="downloadAttachment(attachment)">
                    <v-list-item-icon>
                      <v-icon>mdi-download-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('attachments.command_download') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item link @click="deleteAttachment(attachment)">
                    <v-list-item-icon >
                      <v-icon color="error">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="error--text">
                      {{ $t('attachments.command_delete') }}
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <UploadFileDialog
      :dialog.sync="showUploadAttachmentsDialog"
      :description="$t('attachments.drag_and_drop_attachments')"
      :multiple="true"
      @filesUploaded="processAttachmentUpload($event)">
    </UploadFileDialog>
    <confirm-dialog :show-dialog="this.showConfirmDeleteDialog"
      confirm-color="error"
      :question-text="$t('common_actions.confirm_delete')"
      :positive-text="$t('common_actions.delete')"
      :negative-text="$t('common_actions.cancel')"
      @onCancel="cancelDeleteItem"
      @onConfirm="confirmDeleteItem">
    </confirm-dialog>
    <v-snackbar v-model="showUploadErrorSnackbar" color="error">
      {{ $t('common_error.failed_to_upload_file') }}
    </v-snackbar>
  </v-container>
</template>

<script>
import UploadFileDialog from '@/components/common/UploadFileDialog.vue'
import Thumbnail from '@/components/common/Thumbnail.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import FileService from '@/services/file/FileService'

import api from '@/services/api/api'

export default {
  components: { UploadFileDialog, Thumbnail, ConfirmDialog },
  data () {
    return {
      showUploadAttachmentsDialog: false,
      showUploadErrorSnackbar: false,
      showConfirmDeleteDialog: false,
      attachmentToDelete: null,
      content_application_pdf: require('../../assets/contentTypes/icons8-pdf-48.png'),
      content_application_excel: require('../../assets/contentTypes/icons8-microsoft-excel-48.png'),
      content_application_word: require('../../assets/contentTypes/icons8-microsoft-word-48.png'),
      content_application_txt: require('../../assets/contentTypes/icons8-txt-48.png'),
      content_unknown: require('../../assets/contentTypes/icons8-file-48.png')
    }
  },
  computed: {
    currentArtwork () {
      return this.$store.getters['artworks/currentArtwork']
    }
  },
  methods: {
    processAttachmentUpload (attachmentList) {
      this.$store.dispatch('artworks/createUploadSession').then(
        _ => {
          var promises = []
          attachmentList.forEach(a => promises.push(this.$store.dispatch('artworks/uploadAttachment', a)))
          Promise.all(promises).then(() => {
            console.log('Upload finished!!!')
          }, () => {
            this.showUploadErrorSnackbar = true
          })

          this.$emit('onChange')
        })
    },
    setExportable (attachment) {
      attachment.isExportable = true
      this.$emit('onChange')
    },
    unsetExportable (attachment) {
      attachment.isExportable = false
      this.$emit('onChange')
    },
    deleteAttachment (photo) {
      this.attachmentToDelete = photo
      this.showConfirmDeleteDialog = true
    },
    cancelDeleteItem () {
      this.showConfirmDeleteDialog = false
      this.attachmentToDelete = null
    },
    confirmDeleteItem () {
      this.currentArtwork.attachments.splice(this.currentArtwork.attachments.indexOf(this.attachmentToDelete), 1)

      this.showConfirmDeleteDialog = false
      this.attachmentToDelete = null

      this.$emit('onChange')
    },
    getImageByContentType (contentType) {
      switch (contentType) {
        case 'application/pdf':
          return this.content_application_pdf
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return this.content_application_excel
        case 'application/vnd.ms-word':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return this.content_application_word
        case 'text/plain':
          return this.content_application_txt
        default:
          return this.content_unknown
      }
    },
    downloadAttachment (attachment) {
      FileService.openDownload(attachment.fileId).then(tempId => {
        window.open(api.defaults.baseURL + '/download/' + tempId)
      })
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/components/attachments/AttachmentList.vue